import { City, CityStatusEnum, CityStatusList, CitySupportedFeatureList, UpsertCity } from '@/models/city.model';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import { upsertCity } from '@/services/city.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { DrawerProps, Form, Schema, SelectPicker, FormInstance, NumberInput, Divider, Stack, IconButton, CheckPicker, Grid, Row, Col } from 'rsuite';
import { getCountriesLookup } from '@/services/country.service';
import ErrorMessage from '@/components/ErrorMessage';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import MapMultipleSelector, { MarkerInfo } from '@/components/MapMultipleSelector';
import { languages } from '@/models/language.model';

const modelSchema = Schema.Model<UpsertCity>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  code: Schema.Types.StringType().isRequired('This field is required.'),
  imageUrl: Schema.Types.StringType(),
  countryId: Schema.Types.NumberType().isRequired('This field is required.'),
  lat: Schema.Types.NumberType().isRequired('This field is required.'),
  lon: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  ianaTimeZoneId: Schema.Types.StringType().isRequired('This field is required.'),
});

const emptyModel: UpsertCity = {
  name: '',
  countryId: undefined,
  id: undefined,
  code: '',
  imageUrl: '',
  lat: undefined,
  lon: undefined,
  status: CityStatusEnum.Enable,
  ianaTimeZoneId: '',
  supportedFeatures: []
};

interface CityDrawerProps extends DrawerProps{
  countryId?: number;
  formObject?: City;
  pageState: PageState};

const CityDrawerView = (props: CityDrawerProps) => {
  const { countryId, formObject, pageState } = props;
  const [formValue, setFormValue] = useState<UpsertCity>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, countryId});
      return;
    }

    if (formObject) {
      const castedModel: UpsertCity = { 
        ...formObject,
        countryId: formObject.country?.id,
        supportedFeatures: formObject.supportedFeatures ?? [],
        languageCode: formObject.languageCode ?? undefined,
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  },[formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<City> = await upsertCity(formValue);
      if (result.success) {
        if(countryId){
          nav(`/countries/${formValue.countryId}`, {state:{tab: "country-cities"}});
        }else{
          nav(`/cities`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue, 
      ...value 
    }));
  };

  const getMapValues = (): MarkerInfo[] => {
    const values : MarkerInfo[] = [];
    if(formValue.lat && formValue.lon){
      values.push({
        key:'city', 
        position:{
          lat: Number(formValue.lat), 
          lng: Number(formValue.lon)
        }
      });
    }
    return values;
  };

  const handleOnMapClick = (position: LatLng) => {
    if(!formValue.lat || !formValue.lon){
      setFormValue(pre => ({...pre, lat: position.lat, lon: position.lng}));
    }
  };

  const handleMarkerDrag = (key: string, position: LatLng) => {
    if(key == 'city'){
      setFormValue(pre => ({...pre, lat: position.lat, lon: position.lng}));
    }
  };
  
  return (
    <PageContent>
      <Form name='upsert-city' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={modelSchema}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" value={formValue.name ?? ''}/>
              </Form.Group>
              <Form.Group>
                <Form.Label>Country</Form.Label>
                <Form.Control name="countryId" accepter={FetchingSelect} block
                  fetchData={(text, page) => getCountriesLookup(text, page)} 
                  placeholder="Select a country"
                  disabled={!!countryId}
                />  
              </Form.Group>
              <Row>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Code</Form.Label>
                    <Form.Control name="code" value={formValue.code ?? ''}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:12}}>
                  <Form.Group>
                    <Form.Label>Time Zone Id (IANA)</Form.Label>
                    <Form.Control name="ianaTimeZoneId" />
                    <Form.HelpText >ex: Asia/Hong_Kong</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control name="lat" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control name="lon" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
              </Row>              
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Control name="status" accepter={SelectPicker} data={CityStatusList} block />
              </Form.Group>
              <Form.Group>
                <Form.Label>Supported Features</Form.Label>
                <Form.Control name="supportedFeatures" accepter={CheckPicker} data={CitySupportedFeatureList} searchable={false} block />
              </Form.Group>
              <Form.Group>
                <Form.Label>Content language</Form.Label>
                <Form.Control name="languageCode" accepter={SelectPicker} data={languages} searchable={false} block placeholder="English" />
                <Form.HelpText>The language the app shows multi-language content in for this city (e.g. Energy Hub names and offers), falling back to English.</Form.HelpText>
              </Form.Group>
              <Form.Group>
                <Form.Label>Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="imageUrl"
                  height="250px"
                  url={formValue.imageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.City}
                  onChange={url => setFormValue(pre => ({...pre, imageUrl: url}))}              
                />
              </Form.Group>
            </Col>
            <Col span={{xs:24, xl:16}}>
              <Form.Group>
                <Form.Label style={{ textAlign: 'center'}}>{
                  (!formValue.lat || !formValue.lon) ? 
                  'Click on map to select  City location' : 
                  'Drag Marker to update City location'
                }</Form.Label>
                <MapMultipleSelector
                  value={getMapValues()}
                  onClick={handleOnMapClick}
                  onMarkerDrag={handleMarkerDrag}
                  height='700px'
                />                  
              </Form.Group>
            </Col>
          </Row>
          {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
        </Grid>
      </Form>
      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default CityDrawerView;
